import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import {
  APP_DISPLAY_NAME,
  LOGO_GRENZEN,
  PERSONALISIERUNG_GRENZEN,
  personalisierungLogoPfad,
} from '@mcikm-vote/shared';
import type { LogoAngabe, LogoVariante, PersonalisierungAntwort } from '@mcikm-vote/shared';

import {
  useLogoEntfernen,
  useLogoHochladen,
  usePersonalisierungSpeichern,
  usePersonalisierungVerwaltung,
} from '../../admin/personalisierungApi';
import { Fehlermeldung } from '../../components/Fehlermeldung';
import { Feld } from '../../components/Feld';
import { Karte } from '../../components/Karte';
import { Knopf } from '../../components/Knopf';
import { RechtstextEditor } from '../../components/RechtstextEditor';

/**
 * Personalisierung der Anwendung (Änderungsanforderung ÄA-5).
 *
 * Vier Abschnitte: der Fußzeilenverweis, Impressum, Datenschutzerklärung und die beiden
 * Firmenlogos. Alles auf **einer** Seite und mit **einem** Speichern für die Texte — die Angaben
 * werden zusammen gepflegt, und drei getrennte Formulare wären drei Gelegenheiten, eines zu
 * vergessen.
 *
 * Die Logos sind davon ausgenommen: Ein Upload wirkt sofort. Eine Datei in einem Formular
 * zwischenzuhalten, bis jemand „Speichern" drückt, wäre eine unnötige Fehlerquelle.
 */
export function PersonalisierungSeite(): ReactElement {
  const abfrage = usePersonalisierungVerwaltung();
  const speichern = usePersonalisierungSpeichern();

  const [feedbackUrl, setFeedbackUrl] = useState('');
  const [impressumMarkdown, setImpressumMarkdown] = useState('');
  const [datenschutzMarkdown, setDatenschutzMarkdown] = useState('');
  const [gespeichert, setGespeichert] = useState(false);

  const stand = abfrage.data;

  useEffect(() => {
    if (stand !== undefined) {
      setFeedbackUrl(stand.feedbackUrl);
      setImpressumMarkdown(stand.impressumMarkdown);
      setDatenschutzMarkdown(stand.datenschutzMarkdown);
    }
  }, [stand]);

  if (abfrage.isError) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold">Personalisierung</h1>
        <Fehlermeldung>{abfrage.error.message}</Fehlermeldung>
      </div>
    );
  }

  if (stand === undefined) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold">Personalisierung</h1>
        <p className="text-text-gedaempft text-sm">Wird geladen …</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold">Personalisierung</h1>
      <p className="text-text-gedaempft text-sm">
        Diese Angaben erscheinen für alle Besucher — auch vor der Anmeldung. Impressum und
        Datenschutzerklärung werden als Markdown gepflegt; eine vorhandene <code>.md</code>-Datei
        lässt sich unverändert einsetzen.
      </p>

      <form
        className="flex flex-col gap-4"
        onSubmit={(ereignis) => {
          ereignis.preventDefault();
          setGespeichert(false);
          speichern.mutate(
            { feedbackUrl, impressumMarkdown, datenschutzMarkdown },
            { onSuccess: () => setGespeichert(true) },
          );
        }}
      >
        <Karte>
          <h2 className="text-base font-semibold">Fußzeile</h2>
          <p className="text-text-gedaempft mt-1 text-sm">
            Ziel des Verweises „Kontakt &amp; Feedback". Bleibt das Feld leer, führt der Verweis auf
            die mitgelieferte Adresse.
          </p>
          <div className="mt-3">
            <Feld
              name="feedbackUrl"
              beschriftung="Adresse für „Kontakt & Feedback“"
              wert={feedbackUrl}
              grenze={PERSONALISIERUNG_GRENZEN.feedbackUrl}
              typ="url"
              hinweis="Vollständige Adresse mit http:// oder https://"
              onAendern={setFeedbackUrl}
            />
          </div>
        </Karte>

        <Karte>
          <h2 className="mb-3 text-base font-semibold">Impressum</h2>
          <RechtstextEditor
            name="impressum"
            beschriftung="Inhalt des Impressums"
            wert={impressumMarkdown}
            grenze={PERSONALISIERUNG_GRENZEN.impressum}
            onAendern={setImpressumMarkdown}
          />
        </Karte>

        <Karte>
          <h2 className="mb-3 text-base font-semibold">Datenschutz</h2>
          <RechtstextEditor
            name="datenschutz"
            beschriftung="Inhalt der Datenschutzerklärung"
            wert={datenschutzMarkdown}
            grenze={PERSONALISIERUNG_GRENZEN.datenschutz}
            onAendern={setDatenschutzMarkdown}
          />
        </Karte>

        {speichern.isError && <Fehlermeldung>{speichern.error.message}</Fehlermeldung>}
        {gespeichert && !speichern.isPending && (
          <p role="status" className="text-sm font-medium">
            Gespeichert.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Knopf type="submit" disabled={speichern.isPending}>
            {speichern.isPending ? 'Wird gespeichert …' : 'Speichern'}
          </Knopf>
        </div>
      </form>

      <LogoAbschnitt stand={stand} />
    </div>
  );
}

/**
 * Die beiden Firmenlogos (§21).
 *
 * Getrennt vom Textformular, weil ein Upload sofort wirkt. Jede Variante zeigt das aktuelle Logo
 * auf dem Grund, für den es gedacht ist — ein helles Logo auf hellem Grund ist der häufigste
 * Fehler, und er fällt nur auf, wenn man ihn sieht.
 */
function LogoAbschnitt({ stand }: { stand: PersonalisierungAntwort }): ReactElement {
  const hochladen = useLogoHochladen();
  const entfernen = useLogoEntfernen();

  return (
    <Karte>
      <h2 className="text-base font-semibold">Firmenlogo</h2>
      <p className="text-text-gedaempft mt-1 text-sm">
        Erscheint in der Kopfzeile und über beiden Anmeldeseiten anstelle des
        {` ${APP_DISPLAY_NAME}`}-Markenlogos. Ohne eigenes Logo bleibt alles wie bisher. In der
        Fußzeile steht unverändert das {APP_DISPLAY_NAME}-Markenlogo.
      </p>
      <p className="text-text-gedaempft mt-1 text-sm">
        Zulässig sind PNG, JPEG, WebP und SVG bis {Math.round(LOGO_GRENZEN.maxBytes / 1024)} KB.
      </p>

      {(hochladen.isError || entfernen.isError) && (
        <div className="mt-3">
          <Fehlermeldung>
            {(hochladen.error ?? entfernen.error)?.message ?? 'Der Vorgang ist fehlgeschlagen.'}
          </Fehlermeldung>
        </div>
      )}

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <LogoFeld
          variante="hell"
          beschriftung="Light-Mode"
          angabe={stand.logoHell}
          stand={stand.stand}
          beschaeftigt={hochladen.isPending || entfernen.isPending}
          onDatei={(datei) => hochladen.mutate({ variante: 'hell', datei })}
          onEntfernen={() => entfernen.mutate('hell')}
        />
        <LogoFeld
          variante="dunkel"
          beschriftung="Dark-Mode"
          angabe={stand.logoDunkel}
          stand={stand.stand}
          beschaeftigt={hochladen.isPending || entfernen.isPending}
          onDatei={(datei) => hochladen.mutate({ variante: 'dunkel', datei })}
          onEntfernen={() => entfernen.mutate('dunkel')}
        />
      </div>
    </Karte>
  );
}

function LogoFeld({
  variante,
  beschriftung,
  angabe,
  stand,
  beschaeftigt,
  onDatei,
  onEntfernen,
}: {
  variante: LogoVariante;
  beschriftung: string;
  angabe: LogoAngabe | null;
  stand: string;
  beschaeftigt: boolean;
  onDatei: (datei: File) => void;
  onEntfernen: () => void;
}): ReactElement {
  const feldname = `logo-${variante}`;

  return (
    <div className="border-rand flex flex-col gap-2 rounded-lg border p-3">
      <h3 className="text-sm font-medium">{beschriftung}</h3>

      <div
        // Feste Farben statt Designtokens: Die Vorschau soll den Grund des jeweiligen
        // Erscheinungsbilds zeigen, nicht den gerade eingestellten.
        className={`flex min-h-20 items-center justify-center rounded-md p-3 ${
          variante === 'hell' ? 'bg-white' : 'bg-neutral-900'
        }`}
      >
        {angabe === null ? (
          <span
            className={`text-sm ${variante === 'hell' ? 'text-neutral-500' : 'text-neutral-400'}`}
          >
            Kein eigenes Logo
          </span>
        ) : (
          <img
            src={personalisierungLogoPfad(variante, stand)}
            alt={`Firmenlogo für den ${beschriftung}`}
            className="h-16 w-auto"
          />
        )}
      </div>

      {angabe !== null && (
        <p className="text-text-gedaempft text-xs">
          {angabe.dateiname} — {angabe.typ}, {Math.round(angabe.groesse / 1024)} KB
        </p>
      )}

      <label htmlFor={feldname} className="text-sm">
        Datei auswählen
      </label>
      <input
        id={feldname}
        name={feldname}
        type="file"
        accept={LOGO_GRENZEN.typen.join(',')}
        disabled={beschaeftigt}
        className="text-sm"
        onChange={(ereignis) => {
          const datei = ereignis.target.files?.[0];
          if (datei !== undefined) {
            onDatei(datei);
          }
          // Zurücksetzen, damit dieselbe Datei erneut ausgewählt werden kann.
          ereignis.target.value = '';
        }}
      />

      {angabe !== null && (
        <Knopf
          art="sekundaer"
          className="min-h-0 px-3 py-1 text-sm"
          disabled={beschaeftigt}
          onClick={onEntfernen}
        >
          Logo entfernen
        </Knopf>
      )}
    </div>
  );
}
